/**
 * Main transaction filters component
 * Combines all filter UI elements (search, date range, type/category/group, user/account filters)
 * Extracted from Activity.tsx to improve code organization
 */

import { ReactElement, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  ListItemText,
  FormControlLabel,
  Checkbox,
  Button,
  Chip,
  Stack,
  Collapse,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import TransactionSearch from "./TransactionSearch";
import ActivityPeriodSelector from "./ActivityPeriodSelector";
import DateRangePicker from "./DateRangePicker";
import FilterButtons, { FilterAccount } from "./FilterButtons";
import { TransactionCategoryGroup } from "../../../types";
import { TransactionWithDate } from "../../../utils/transactionProcessing";

export interface TransactionFiltersProps {
  // Search
  searchQuery: string;
  onSearchChange: (query: string) => void;
  searchExpanded: boolean;
  onSearchExpandedChange: (expanded: boolean) => void;
  
  // Filter values
  typeFilter: string;
  groupFilter: string;
  categoryFilter: string;
  selectedPeriod: string;
  startDate: string;
  endDate: string;
  selectedUsers: string[];
  selectedAccountFilters: string[];
  showOnlyEdited: boolean;
  
  // Filter handlers
  onTypeFilterChange: (type: string) => void;
  onGroupFilterChange: (group: string) => void;
  onCategoryFilterChange: (category: string) => void;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onPeriodChange: (period: string) => void;
  onPeriodNavigation: (direction: "left" | "right") => void;
  onUsersChange: (users: string[]) => void;
  onAccountFiltersChange: (filters: string[]) => void;
  onShowOnlyEditedChange: (show: boolean) => void;
  onClearAllFilters: () => void;
  
  // Date picker state
  focusedDateField: "startDate" | "endDate" | null;
  onFocusedDateFieldChange: (field: "startDate" | "endDate" | null) => void;
  
  // Available options
  availableTypes: readonly string[];
  availableGroups: TransactionCategoryGroup[];
  availableCategories: string[];
  
  // Data
  accounts: FilterAccount[];
  workspaceMembers: Array<{ id: string; name?: string; email?: string }>;
  transactions?: Array<{ userId?: string }>; // Optional: to check which users have created transactions (minimal type needed)
  
  // Helpers
  getAccountDisplayName: (accountId: string) => string;
  getUserName: (userId: string) => string;
  
  // Mobile state
  filtersExpanded: boolean;
  onFiltersExpandedChange: (expanded: boolean) => void;

  /** When filters are active, show how many transactions match (displayed in top right) */
  filteredCount?: number;

  /** Whether any filters are active (use parent's logic - considers Monthly+current month as default) */
  hasActiveFilters?: boolean;
}

/**
 * Main transaction filters component
 */
export default function TransactionFilters({
  searchQuery,
  onSearchChange,
  searchExpanded,
  onSearchExpandedChange,
  typeFilter,
  groupFilter,
  categoryFilter,
  selectedPeriod,
  startDate,
  endDate,
  selectedUsers,
  selectedAccountFilters,
  showOnlyEdited,
  onTypeFilterChange,
  onGroupFilterChange,
  onCategoryFilterChange,
  onStartDateChange,
  onEndDateChange,
  onPeriodChange,
  onPeriodNavigation,
  onUsersChange,
  onAccountFiltersChange,
  onShowOnlyEditedChange,
  onClearAllFilters,
  focusedDateField,
  onFocusedDateFieldChange,
  availableTypes,
  availableGroups,
  availableCategories,
  accounts,
  workspaceMembers,
  transactions = [],
  getAccountDisplayName,
  getUserName,
  filtersExpanded,
  onFiltersExpandedChange,
  filteredCount,
  hasActiveFilters: hasActiveFiltersProp,
}: TransactionFiltersProps): ReactElement {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [periodMenuAnchor, setPeriodMenuAnchor] = useState<null | HTMLElement>(null);

  // Use parent's hasActiveFilters when provided (considers Monthly+current month as default)
  // Fallback to local computation only when prop is omitted (e.g. other consumers)
  const hasActiveFilters = useMemo(() => {
    if (hasActiveFiltersProp !== undefined) return hasActiveFiltersProp;
    const userFilterActive = selectedUsers.length > 0 && selectedUsers.length < workspaceMembers.length;
    const allAccountsSelected = accounts.length > 0 &&
      (selectedAccountFilters.length === 0 || selectedAccountFilters.length === accounts.length);
    return (
      searchQuery !== "" ||
      typeFilter !== "All" ||
      groupFilter !== "All" ||
      categoryFilter !== "All" ||
      (accounts.length > 0 && !allAccountsSelected) ||
      startDate !== "" ||
      endDate !== "" ||
      userFilterActive ||
      showOnlyEdited
    );
  }, [hasActiveFiltersProp, searchQuery, typeFilter, groupFilter, categoryFilter, selectedAccountFilters.length, accounts.length, startDate, endDate, selectedUsers.length, showOnlyEdited, workspaceMembers.length]);

  if (isMobile) {
    // Mobile layout - period at top (like Analytics), then filter bar, then collapsible filters
    return (
      <Paper
        elevation={1}
        sx={{
          px: 1,
          py: 0.75,
          mb: 1,
          borderRadius: 0.75,
          position: "sticky",
          top: 0,
          zIndex: 10,
          bgcolor: "background.paper",
        }}
      >
        {/* Top bar with search and filter icons */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
          <TransactionSearch
            searchQuery={searchQuery}
            onSearchChange={onSearchChange}
            searchExpanded={searchExpanded}
            onSearchExpandedChange={onSearchExpandedChange}
          />
          <IconButton
            onClick={() => onFiltersExpandedChange(!filtersExpanded)}
            sx={{ p: 0.5 }}
            color={hasActiveFilters ? "primary" : "default"}
          >
            <FilterListIcon sx={{ fontSize: 22 }} />
          </IconButton>
          {hasActiveFilters && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, ml: "auto" }}>
              {filteredCount !== undefined && (
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, flexShrink: 0 }}>
                  {filteredCount} {filteredCount === 1 ? "item" : "items"}
                </Typography>
              )}
              <Chip
                label="Clear All"
                size="small"
                onClick={onClearAllFilters}
                color="primary"
                variant="filled"
                sx={{ fontWeight: "medium", height: 24, "& .MuiChip-label": { px: 1, fontSize: "0.75rem" } }}
              />
            </Box>
          )}
        </Box>

        {/* Period row - margin animates in sync with Collapse so open/close feels like one motion */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            width: "100%",
            mb: filtersExpanded ? 1 : 0,
            justifyContent: "center",
            py: 0.5,
            transition: "margin-bottom 0.18s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <IconButton
            size="small"
            onClick={() => onPeriodNavigation("left")}
            disabled={selectedPeriod === "Custom"}
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              minWidth: 40,
              minHeight: 40,
              opacity: selectedPeriod === "All time" ? 0.5 : 1,
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <Box sx={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center", minWidth: 0 }}>
            {selectedPeriod === "Custom" ? (
              <DateRangePicker
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={onStartDateChange}
                onEndDateChange={onEndDateChange}
                focusedDateField={focusedDateField}
                onFocusedDateFieldChange={onFocusedDateFieldChange}
              />
            ) : (
              <Box
                onClick={(e) => setPeriodMenuAnchor(e.currentTarget as HTMLElement)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  cursor: "pointer",
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <Typography variant="body1" fontWeight="medium" sx={{ fontSize: "0.95rem", userSelect: "none", textAlign: "center" }}>
                  {(() => {
                    if (!startDate || !endDate) return "";
                    const from = new Date(startDate);
                    const to = new Date(endDate);
                    if (selectedPeriod === "All time") return "All time";
                    if (selectedPeriod === "Daily") return from.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
                    if (selectedPeriod === "Weekly") return `${from.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${to.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
                    if (selectedPeriod === "Monthly") return from.toLocaleDateString("en-US", { month: "short", year: "numeric" });
                    if (selectedPeriod === "Yearly") return from.getFullYear().toString();
                    return "";
                  })()}
                </Typography>
                <ArrowDropDownIcon sx={{ fontSize: "1.2rem", opacity: selectedPeriod === "All time" ? 0.3 : 0.7 }} />
              </Box>
            )}
          </Box>
          <Menu
            anchorEl={periodMenuAnchor}
            open={Boolean(periodMenuAnchor)}
            onClose={() => setPeriodMenuAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <MenuItem onClick={() => { onPeriodChange("Daily"); setPeriodMenuAnchor(null); }}><ListItemText primary="Daily" /></MenuItem>
            <MenuItem onClick={() => { onPeriodChange("Weekly"); setPeriodMenuAnchor(null); }}><ListItemText primary="Weekly" /></MenuItem>
            <MenuItem onClick={() => { onPeriodChange("Monthly"); setPeriodMenuAnchor(null); }}><ListItemText primary="Monthly" /></MenuItem>
            <MenuItem onClick={() => { onPeriodChange("Yearly"); setPeriodMenuAnchor(null); }}><ListItemText primary="Yearly" /></MenuItem>
            <MenuItem onClick={() => { onPeriodChange("All time"); setPeriodMenuAnchor(null); }}><ListItemText primary="All time" /></MenuItem>
            <MenuItem onClick={() => { onPeriodChange("Custom"); setPeriodMenuAnchor(null); }}><ListItemText primary="Custom Period" /></MenuItem>
          </Menu>
          <IconButton
            size="small"
            onClick={() => onPeriodNavigation("right")}
            disabled={selectedPeriod === "Custom"}
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              minWidth: 40,
              minHeight: 40,
              opacity: selectedPeriod === "All time" ? 0.5 : 1,
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
        
        {/* Filter panel - one smooth motion with period row (same duration + easing) */}
        <Collapse
          in={filtersExpanded}
          timeout={{ enter: 180, exit: 150 }}
          easing={{ enter: "cubic-bezier(0.4, 0, 0.2, 1)", exit: "cubic-bezier(0.4, 0, 0.2, 1)" }}
          sx={{
            "& .MuiCollapse-wrapper": { willChange: "height" },
            "& .MuiCollapse-wrapperInner": { display: "block" },
          }}
        >
          <Box sx={{ pt: 1, pb: focusedDateField ? "420px" : 0 }}>
            <Stack spacing={1.5}>
              <Grid container spacing={1.5}>
                <Grid size={6}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={typeFilter}
                      label="Type"
                      onChange={(e) => onTypeFilterChange(e.target.value)}
                    >
                      {availableTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={6}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Group</InputLabel>
                    <Select
                      value={groupFilter}
                      label="Group"
                      onChange={(e) => onGroupFilterChange(e.target.value)}
                    >
                      <MenuItem value="All">All Groups</MenuItem>
                      {availableGroups
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((group) => (
                          <MenuItem key={group.id} value={group.name}>
                            {group.name}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
                 <Grid size={6}>
                   <FormControl size="small" fullWidth>
                     <InputLabel>Category</InputLabel>
                     <Select
                       value={categoryFilter}
                       label="Category"
                       onChange={(e) => onCategoryFilterChange(e.target.value)}
                     >
                       <MenuItem value="All">All Categories</MenuItem>
                       {availableCategories.map((category) => (
                         <MenuItem key={category} value={category}>
                           {category}
                         </MenuItem>
                       ))}
                     </Select>
                   </FormControl>
                 </Grid>
                 {/* Account filter - right side of Category */}
                 {accounts.length > 0 && (
                   <Grid size={6}>
                     <FilterButtons
                       accounts={accounts}
                       selectedAccountFilters={selectedAccountFilters}
                       onAccountFiltersChange={onAccountFiltersChange}
                       getAccountDisplayName={getAccountDisplayName}
                       workspaceMembers={[]}
                       selectedUsers={[]}
                       onUsersChange={() => {}}
                       getUserName={() => ""}
                       showOnlyEdited={false}
                       onShowOnlyEditedChange={() => {}}
                       showAccountFilter={true}
                       showUserFilter={false}
                       showEditedCheckbox={false}
                     />
                   </Grid>
                 )}
                 {/* Edited checkbox - period is at top, outside collapse */}
                 <Grid size={12}>
                   <FormControlLabel
                     control={
                       <Checkbox
                         checked={showOnlyEdited}
                         onChange={(e) => onShowOnlyEditedChange(e.target.checked)}
                       />
                     }
                     label="Edited"
                   />
                 </Grid>
                {/* User filter and Edited checkbox - always show */}
                <Grid size={6}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <FilterButtons
                      accounts={[]}
                      selectedAccountFilters={[]}
                      onAccountFiltersChange={() => {}}
                      getAccountDisplayName={() => ""}
                      workspaceMembers={workspaceMembers}
                      selectedUsers={selectedUsers}
                      onUsersChange={onUsersChange}
                      getUserName={getUserName}
                      showOnlyEdited={showOnlyEdited}
                      onShowOnlyEditedChange={onShowOnlyEditedChange}
                      showAccountFilter={false}
                      showUserFilter={true}
                      showEditedCheckbox={false}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Stack>
          </Box>
        </Collapse>
      </Paper>
    );
  }

  // Desktop layout
  return (
    <Paper
      elevation={1}
      sx={{
        p: 1.5,
        mb: 2,
        position: "sticky",
        top: 0,
        zIndex: 10,
        bgcolor: "background.paper",
      }}
    >
      <Grid container spacing={1.5} alignItems="center">
        <Grid size={{ xs: 12, sm: 6, md: "auto" }}>
          <TransactionSearch
            searchQuery={searchQuery}
            onSearchChange={onSearchChange}
            searchExpanded={searchExpanded}
            onSearchExpandedChange={onSearchExpandedChange}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: "auto" }}>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={typeFilter}
              label="Type"
              onChange={(e) => onTypeFilterChange(e.target.value)}
            >
              {availableTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: "auto" }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Group</InputLabel>
            <Select
              value={groupFilter}
              label="Group"
              onChange={(e) => onGroupFilterChange(e.target.value)}
            >
              <MenuItem value="All">All Groups</MenuItem>
              {availableGroups
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((group) => (
                  <MenuItem key={group.id} value={group.name}>
                    {group.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: "auto" }}>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              label="Category"
              onChange={(e) => onCategoryFilterChange(e.target.value)}
            >
              <MenuItem value="All">All Categories</MenuItem>
              {availableCategories.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {/* Account filter */}
        {accounts.length > 0 && (
          <Grid size={{ xs: 12, sm: 6, md: "auto" }}>
            <FilterButtons
              accounts={accounts}
              selectedAccountFilters={selectedAccountFilters}
              onAccountFiltersChange={onAccountFiltersChange}
              getAccountDisplayName={getAccountDisplayName}
              workspaceMembers={[]}
              selectedUsers={[]}
              onUsersChange={() => {}}
              getUserName={() => ""}
              showOnlyEdited={false}
              onShowOnlyEditedChange={() => {}}
              showAccountFilter={true}
              showUserFilter={false}
              showEditedCheckbox={false}
            />
          </Grid>
        )}
        {/* Period selector + date range (same logic as Analytics) */}
        <Grid size={{ xs: 12, sm: 6, md: "auto" }}>
          <ActivityPeriodSelector
            selectedPeriod={selectedPeriod}
            onPeriodChange={onPeriodChange}
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={onStartDateChange}
            onEndDateChange={onEndDateChange}
            onPeriodNavigation={onPeriodNavigation}
            focusedDateField={focusedDateField}
            onFocusedDateFieldChange={onFocusedDateFieldChange}
          />
        </Grid>
        {/* User filter - always show (matches Analytics) */}
        <Grid size={{ xs: 12, sm: 6, md: "auto" }}>
          <FilterButtons
            accounts={[]}
            selectedAccountFilters={[]}
            onAccountFiltersChange={() => {}}
            getAccountDisplayName={() => ""}
            workspaceMembers={workspaceMembers}
            selectedUsers={selectedUsers}
            onUsersChange={onUsersChange}
            getUserName={getUserName}
            transactions={transactions}
            showOnlyEdited={showOnlyEdited}
            onShowOnlyEditedChange={onShowOnlyEditedChange}
            showAccountFilter={false}
            showUserFilter={true}
            showEditedCheckbox={true}
          />
        </Grid>
        {/* Top right: filtered count (new item, only when filters active) */}
        {hasActiveFilters && filteredCount !== undefined && (
          <Grid size="auto" sx={{ display: "flex", alignItems: "center", ml: "auto" }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {filteredCount} {filteredCount === 1 ? "item" : "items"}
            </Typography>
          </Grid>
        )}
        {hasActiveFilters && (
          <Grid size={{ xs: 12, sm: 6, md: "auto" }}>
            <Button
              variant="outlined"
              size="small"
              onClick={onClearAllFilters}
              startIcon={<ClearIcon />}
              sx={{
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  bgcolor: "action.hover",
                  transform: "translateY(-1px)",
                },
              }}
            >
              Clear All
            </Button>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
}
